/**
 * RWBR — turning a POST RACE INTERVIEW result into something the next race can
 * actually feel.
 *
 * `postRaceInterview.ts` owns the chart: 36 rows, who is eligible, what the
 * ACTION TAKEN cell says. This module owns the *consequence* — it is the piece
 * that was missing while the chart sat built-but-unwired.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  D-EZ-15 — HOW LONG AN ACTION TAKEN LASTS. Settled from the printed sheet.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This was carried for two sessions as an open ruling — "the sheet states a
 * duration for some effects and not for others". Reading the two printed lines
 * together settles it without a house rule.
 *
 *   1. THE PROCEDURE LINE, verbatim: *"At end of race, roll two dice. Choose
 *      random driver in that category and apply ACTION TAKEN result **for next
 *      race**."*
 *
 *   2. EVERY CLARIFICATION THAT NAMES A DURATION NAMES ONE INSIDE A SINGLE
 *      RACE: "For the first third of the race"; "(one time)"; "On first
 *      ELIGIBLE pit roll"; "first escape roll". Not one of them reaches past a
 *      checkered flag.
 *
 * So the sheet is not silent and it does not contradict itself. The procedure
 * line scopes EVERY action to the next race; the clarifications then scope some
 * of them more tightly still, to a phase or a single use inside that race.
 * Nothing on this sheet is a permanent card change, and nothing here invents
 * one.
 *
 * The consequence worth stating plainly: **a driver's printed card is never
 * rewritten by an interview.** "Gain NEW Quality" gives him that quality for
 * one race and it is gone by the race after. That is what makes the chart safe
 * to fire 36 times a season — the published card set still looks like itself in
 * November — and it is the reading with printed evidence behind it rather than
 * the one that felt most dramatic.
 *
 * If a clean re-scan of the sheet ever turns up a "permanently" this ruling
 * dies; `INTERVIEW_DURATION_CITE` is the string to grep for.
 *
 * ── TWO SHAPES OF EFFECT, BECAUSE THE SHEET HAS TWO ────────────────────────
 *
 * A. CARD EDITS that hold for the whole race — a bigger ESCAPE, an extra
 *    quality, a lost gas pedal. Applied to a COPY of the card handed to the
 *    next race weekend. The roster's card is never touched, which is what makes
 *    "one race" true by construction rather than by remembering to undo it.
 *
 * B. GRANTS that are narrower than a race — "the first third", "one time",
 *    "first ELIGIBLE pit roll". These cannot be a card edit, because a card has
 *    no notion of when. They ride on `Car.grants` and are consumed where they
 *    are used.
 *
 * Plus the list operations (TV / FUME / FEUD), which the season layer already
 * has plumbing for, and the one row the sheet hands to a human.
 */

import type { DriverCard, Quality, RaceGrants } from './types.ts';
import { QUALITIES } from './types.ts';
import type { Dice } from './dice.ts';
import type { InterviewAction, InterviewActionKind } from './postRaceInterview.ts';
import {
  BRAKE_MAX,
  GAS_PEDAL_MAX,
  GAS_PEDAL_MIN,
  QUALITIES_MIN,
} from './postRaceInterview.ts';

export const INTERVIEW_DURATION_CITE =
  'D-EZ-15 — RWBR_postraceinterview.pdf procedure line ("apply ACTION TAKEN result for next race") ' +
  'read together with the clarifications block, every duration in which is scoped inside one race ' +
  '("For the first third of the race", "(one time)", "On first ELIGIBLE pit roll", "first escape ' +
  'roll"). Therefore NO interview result is a permanent card change.';

/**
 * EZRA-S2 §3.9 caps a Fast Pit rating at 9. The interview sheet prints no
 * ceiling of its own for Escape, Repair or Fast Pit, so where EZRA states one
 * it governs and where it does not the value is uncapped.
 */
export const FAST_PIT_MAX_EZRA = 9;
export const CITE_FAST_PIT_CAP =
  'EZRA-S2 §3.9 — Fast Pit is capped at 9. The interview sheet prints no ceiling for Escape, ' +
  'Repair or Fast Pit; where EZRA states one it governs.';

// ---------------------------------------------------------------------------
// Grants — the effects a card cannot express
// ---------------------------------------------------------------------------

/**
 * Re-exported from `types.ts`, which declares it so that `Car` can carry it
 * without importing this module. Every RULE about a grant lives here; the
 * declaration lives there for the same import-cycle reason as `TraitCode`.
 */
export type { RaceGrants } from './types.ts';

/** True when nothing is left on the grant — used to drop the object entirely. */
export function grantsAreEmpty(g: RaceGrants | undefined): boolean {
  if (!g) return true;
  return (
    !g.ignoreOutOnce &&
    !g.autoEscapeOnce &&
    !g.autoWinChallengeOnce &&
    !g.fastPitAuto &&
    !g.repairBonus &&
    !g.firstThirdGas &&
    !g.firstThirdBrake &&
    !g.firstDuelFeudFume
  );
}

/**
 * p.ii / Board B — "the first third of the race" is the same third the repair
 * grid already uses. Defined here against the same turn arithmetic so the
 * interview's first-third and the repair table's first-third can never drift
 * apart.
 */
export function isFirstThird(turn: number, totalTurns: number): boolean {
  if (totalTurns <= 0) return false;
  return turn >= 1 && turn <= Math.floor(totalTurns / 3);
}

// ---------------------------------------------------------------------------
// List operations
// ---------------------------------------------------------------------------

export type InterviewListOp =
  | { kind: 'tv'; driverId: string }
  | { kind: 'fume'; driverId: string }
  | { kind: 'feud-ends'; driverId: string }
  | { kind: 'both-to-fume'; driverId: string }
  | { kind: 'first-duel-feud-fume'; driverId: string };

// ---------------------------------------------------------------------------
// The applied effect
// ---------------------------------------------------------------------------

export interface InterviewEffect {
  driverId: string;
  kind: InterviewActionKind;
  /** The ACTION TAKEN cell, verbatim, for the ledger. */
  actionText: string;
  /** The card as it will be for ONE race. Null when the action edits no card. */
  card: DriverCard | null;
  grants: RaceGrants;
  /** Performance chips the driver starts the next race holding. */
  chips: number;
  listOps: InterviewListOp[];
  /** The sheet hands this row to a human; nothing is applied automatically. */
  needsHumanRuling: boolean;
  /** Plain-language account of what actually changed, for the ledger. */
  notes: string[];
  /**
   * Set when the action could not bite — every quality already held, gas pedal
   * already at the floor. Reported rather than silently swallowed: an effect
   * that did nothing must be visible, or the chart looks like it fired when it
   * did not.
   */
  noOp: boolean;
}

function pick<T>(items: readonly T[], dice: Dice): T {
  return items[dice.d(items.length) - 1];
}

/**
 * Apply one ACTION TAKEN to one driver, for one race.
 *
 * The card handed in is never mutated — a copy comes back — because the roster
 * card has to survive the race unchanged for D-EZ-15 to hold by construction.
 */
export function applyInterviewAction(
  card: DriverCard,
  action: InterviewAction,
  dice: Dice,
): InterviewEffect {
  const base: InterviewEffect = {
    driverId: card.driverId,
    kind: action.kind,
    actionText: action.text,
    card: null,
    grants: {},
    chips: 0,
    listOps: [],
    needsHumanRuling: false,
    notes: [],
    noOp: false,
  };
  const next: DriverCard = {
    ...card,
    qualities: [...card.qualities],
    starred: [...card.starred],
  };
  const amount = action.amount ?? 1;
  const who = card.name;

  switch (action.kind) {
    // ---- card edits, whole race -------------------------------------------
    case 'increase-escape': {
      next.escape = card.escape + amount;
      base.card = next;
      base.notes.push(`${who}'s ESCAPE goes ${card.escape} → ${next.escape} for one race.`);
      return base;
    }
    case 'increase-fast-pit': {
      const raw = card.fastPit + amount;
      next.fastPit = Math.min(raw, FAST_PIT_MAX_EZRA);
      base.card = next;
      base.notes.push(
        `${who}'s FAST PIT goes ${card.fastPit} → ${next.fastPit} for one race` +
          (raw > FAST_PIT_MAX_EZRA ? ` (capped at ${FAST_PIT_MAX_EZRA} — ${CITE_FAST_PIT_CAP})` : '') +
          '.',
      );
      if (next.fastPit === card.fastPit) base.noOp = true;
      return base;
    }
    case 'increase-repair': {
      base.grants.repairBonus = amount;
      base.notes.push(
        `${who}'s crew get +${amount} on every REPAIR roll for one race — the card carries no ` +
          'repair number, so this rides on the roll (Board B is indexed by crew grade).',
      );
      return base;
    }
    case 'lose-gas-pedal': {
      if (card.gasPedal <= GAS_PEDAL_MIN) {
        base.noOp = true;
        base.notes.push(
          `${who} is already down to ${card.gasPedal} gas pedal — the sheet's own floor ` +
            `("cannot have less than one Gas Pedal") holds and nothing changes.`,
        );
        return base;
      }
      next.gasPedal = card.gasPedal - 1;
      base.card = next;
      base.notes.push(`${who} loses a gas pedal, ${card.gasPedal} → ${next.gasPedal}, for one race.`);
      return base;
    }
    case 'lose-a-quality': {
      if (card.qualities.length <= QUALITIES_MIN) {
        base.noOp = true;
        base.notes.push(
          `${who} is down to his last quality — the printed floor ("less than one quality") holds.`,
        );
        return base;
      }
      const lost = pick(card.qualities, dice);
      next.qualities = card.qualities.filter((q) => q !== lost);
      next.starred = card.starred.filter((q) => q !== lost);
      base.card = next;
      base.notes.push(`${who} loses ${lost} for one race — a random quality, as the sheet prints.`);
      return base;
    }
    case 'gain-new-quality': {
      const missing = QUALITIES.filter((q) => !card.qualities.includes(q));
      if (missing.length === 0) {
        base.noOp = true;
        base.notes.push(`${who} already carries all six qualities — nothing left to gain.`);
        return base;
      }
      const gained = pick(missing, dice);
      next.qualities = [...card.qualities, gained];
      base.card = next;
      base.notes.push(`${who} picks up ${gained} for one race — "one quality he does not already have".`);
      return base;
    }
    case 'gain-heat': {
      // "Racer gains HEAT quality. If Racer already has HEAT, quality is now Starred."
      if (!card.qualities.includes('HEAT')) {
        next.qualities = [...card.qualities, 'HEAT' as Quality];
        base.card = next;
        base.notes.push(`${who} races with HEAT for one race.`);
        return base;
      }
      if (!card.starred.includes('HEAT')) {
        next.starred = [...card.starred, 'HEAT' as Quality];
        base.card = next;
        base.notes.push(`${who} already had HEAT — it is STARRED for one race, so it challenges twice.`);
        return base;
      }
      base.noOp = true;
      base.notes.push(`${who}'s HEAT is already starred; the sheet prints no third step.`);
      return base;
    }
    case 'star-a-quality': {
      const unstarred = card.qualities.filter((q) => !card.starred.includes(q));
      if (unstarred.length === 0) {
        base.noOp = true;
        base.notes.push(`${who} has a star on every quality already — "(not starred)" leaves nothing.`);
        return base;
      }
      const starred = pick(unstarred, dice);
      next.starred = [...card.starred, starred];
      base.card = next;
      base.notes.push(`${who}'s ${starred} carries a star for one race — a second challenge on it.`);
      return base;
    }

    // ---- grants, narrower than a race --------------------------------------
    case 'gain-gas-pedal': {
      if (card.gasPedal >= GAS_PEDAL_MAX) {
        base.noOp = true;
        base.notes.push(`${who} is already on ${GAS_PEDAL_MAX} gas pedals — the printed ceiling.`);
        return base;
      }
      base.grants.firstThirdGas = 1;
      base.notes.push(`${who} runs the first third on ${card.gasPedal + 1} gas pedals.`);
      return base;
    }
    case 'gain-brake': {
      if (card.brakePedal >= BRAKE_MAX) {
        base.noOp = true;
        base.notes.push(`${who} is already on ${BRAKE_MAX} brakes — the printed ceiling.`);
        return base;
      }
      base.grants.firstThirdBrake = 1;
      base.notes.push(`${who} runs the first third with an extra brake.`);
      return base;
    }
    case 'ignore-out-once': {
      base.grants.ignoreOutOnce = true;
      base.notes.push(`${who} shrugs off the first OUT the Problem or Feud/Fume chart hands him.`);
      return base;
    }
    case 'auto-escape-once': {
      base.grants.autoEscapeOnce = true;
      base.notes.push(`${who}'s first ESCAPE check succeeds automatically.`);
      return base;
    }
    case 'auto-win-quality-challenge': {
      base.grants.autoWinChallengeOnce = true;
      base.notes.push(`${who} wins his first quality challenge outright — chips ignored, as printed.`);
      return base;
    }
    case 'one-successful-fast-pit': {
      base.grants.fastPitAuto = 'single';
      base.notes.push(`${who}'s first eligible FAST PIT succeeds automatically.`);
      return base;
    }
    case 'gain-double-fast-pit': {
      base.grants.fastPitAuto = 'double';
      base.chips = 2;
      base.notes.push(
        `${who}'s first eligible FAST PIT succeeds and doubles — and he is handed 2 performance ` +
          'chips, which the sheet states outright.',
      );
      return base;
    }
    case 'gain-performance-chip': {
      base.chips = 1;
      base.notes.push(`${who} starts the next race holding a performance chip.`);
      return base;
    }

    // ---- lists ------------------------------------------------------------
    case 'tv-list': {
      base.listOps.push({ kind: 'tv', driverId: card.driverId });
      base.notes.push(`${who} goes on the TV list for next race.`);
      return base;
    }
    case 'fume-list': {
      base.listOps.push({ kind: 'fume', driverId: card.driverId });
      base.notes.push(`${who} goes on the FUME list for next race.`);
      return base;
    }
    case 'feud-ends': {
      base.listOps.push({ kind: 'feud-ends', driverId: card.driverId });
      base.notes.push(`${who}'s feud is over — both names come off the list.`);
      return base;
    }
    case 'place-both-on-fume-list': {
      base.listOps.push({ kind: 'both-to-fume', driverId: card.driverId });
      base.notes.push(
        `${who} and the man he was feuding with come off the FEUD list and go onto the FUME list ` +
          'separately — which can start two new feuds with two other drivers.',
      );
      return base;
    }
    case 'first-duel-feud-fume': {
      // BOTH, and they are not duplicates. The list op is the SEASON's record
      // that he carries the effect; the grant is what the RACE can actually act
      // on. Recording it only on the carry is what left this effect inert for
      // three sessions: the ledger said it had been granted and nothing in the
      // turn loop could see it.
      base.listOps.push({ kind: 'first-duel-feud-fume', driverId: card.driverId });
      base.grants.firstDuelFeudFume = true;
      base.notes.push(`${who}'s first duel next race rolls straight onto the Feud/Fume chart.`);
      return base;
    }

    // ---- the one the sheet hands to a human --------------------------------
    case 'pushing-shoving-punches': {
      base.needsHumanRuling = true;
      base.notes.push(
        `Words became fisticuffs. The sheet hands this one to Race Officials — nothing is applied ` +
          'automatically, and it is on the ledger so it cannot be quietly forgotten.',
      );
      return base;
    }
  }
}

/**
 * Apply the first-third pedal grants to a card mid-race.
 *
 * Kept here rather than in `turn.ts` so the ceiling check and the citation live
 * beside the rule they come from. Returns the same object when nothing applies,
 * so callers can compare by identity and skip the work.
 */
export function cardForTurn(
  card: DriverCard,
  grants: RaceGrants | undefined,
  turn: number,
  totalTurns: number,
): DriverCard {
  if (!grants) return card;
  if (!grants.firstThirdGas && !grants.firstThirdBrake) return card;
  if (!isFirstThird(turn, totalTurns)) return card;
  return {
    ...card,
    gasPedal: Math.min(GAS_PEDAL_MAX, card.gasPedal + (grants.firstThirdGas ?? 0)),
    brakePedal: Math.min(BRAKE_MAX, card.brakePedal + (grants.firstThirdBrake ?? 0)),
  };
}
